import React, { useEffect, useState } from "react";
import { api, fmtINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, X, Copy } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "@/components/ImageUploader";
import { API } from "@/lib/api";

const emptyForm = () => ({
  brand: "", model: "", title: "", description: "",
  image_url: "https://images.unsplash.com/photo-1592286927505-1def25115558?auto=format&fit=crop&w=800&q=80",
  images: [],
  featured: false,
  variants: [{ name: "Standard", price_inr: 0, stock: 10, sku: "" }],
});

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const load = () => api.get("/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm()); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      brand: p.brand, model: p.model, title: p.title, description: p.description,
      image_url: p.image_url, images: p.images || [], featured: !!p.featured,
      variants: p.variants.map((v) => ({ name: v.name, price_inr: v.price_inr, stock: v.stock, sku: v.sku || "" })),
    });
    setOpen(true);
  };

  const openCopy = (p) => {
    setEditing(null);
    setForm({
      brand: p.brand,
      model: p.model,
      title: `${p.title} (Copy)`,
      description: p.description,
      image_url: p.image_url,
      images: p.images || [],
      featured: false,
      variants: p.variants.map((v) => ({
        name: v.name,
        price_inr: v.price_inr,
        stock: v.stock,
        sku: v.sku ? `${v.sku}-COPY` : "",
      })),
    });
    setOpen(true);
    toast.info("Editing a copy — change details and click Create");
  };

  const save = async () => {
    if (!form.title || !form.brand || !form.model || form.variants.length === 0) {
      toast.error("Fill all required fields and add at least one variant"); return;
    }
    try {
      if (editing) {
        await api.put(`/admin/products/${editing.id}`, form);
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", form);
        toast.success("Product created");
      }
      setOpen(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.title}?`)) return;
    await api.delete(`/admin/products/${p.id}`);
    toast.success("Deleted"); load();
  };

  const updVar = (i, field, val) => setForm((f) => {
    const v = [...f.variants]; v[i] = { ...v[i], [field]: val }; return { ...f, variants: v };
  });
  const addVar = () => setForm((f) => ({ ...f, variants: [...f.variants, { name: "", price_inr: 0, stock: 10, sku: "" }] }));
  const removeVar = (i) => setForm((f) => ({ ...f, variants: f.variants.filter((_, idx) => idx !== i) }));

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl tracking-tight">Products</h1>
        <Button data-testid="new-product-btn" onClick={openNew} className="bg-blue-600 hover:bg-blue-500 rounded-none">
          <Plus className="h-4 w-4 mr-2"/> New product
        </Button>
      </div>

      <div className="border border-white/10 bg-zinc-950 divide-y divide-white/10">
        {products.map((p) => (
          <div key={p.id} data-testid={`admin-product-${p.id}`} className="p-4 flex items-center gap-4">
            <img src={p.image_url} className="h-16 w-16 object-cover border border-white/10" alt=""/>
            <div className="flex-1 min-w-0">
              <p className="font-display truncate">{p.title}</p>
              <p className="text-xs text-white/50 font-mono">{p.brand} · {p.model} · {p.variants.length} variants · from {fmtINR(Math.min(...p.variants.map((v) => v.price_inr)))}</p>
            </div>
            <Button data-testid={`edit-${p.id}`} variant="ghost" size="icon" onClick={() => openEdit(p)} title="Edit"><Pencil className="h-4 w-4"/></Button>
            <Button data-testid={`copy-${p.id}`} variant="ghost" size="icon" onClick={() => openCopy(p)} title="Duplicate"><Copy className="h-4 w-4"/></Button>
            <Button data-testid={`del-${p.id}`} variant="ghost" size="icon" onClick={() => remove(p)} title="Delete"><Trash2 className="h-4 w-4"/></Button>
          </div>
        ))}
        {products.length === 0 && <div className="p-8 text-center text-white/50 text-sm">No products yet.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-white/10 text-white sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editing ? "Edit product" : "New product"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-white/60">Brand</Label><Input data-testid="pf-brand" value={form.brand} onChange={(e) => setForm({...form, brand: e.target.value})} className="bg-black border-white/10 mt-1"/></div>
              <div><Label className="text-xs text-white/60">Model</Label><Input data-testid="pf-model" value={form.model} onChange={(e) => setForm({...form, model: e.target.value})} className="bg-black border-white/10 mt-1"/></div>
            </div>
            <div><Label className="text-xs text-white/60">Title</Label><Input data-testid="pf-title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="bg-black border-white/10 mt-1"/></div>
            <div>
              <Label className="text-xs text-white/60">Main image URL (optional — used if no uploads)</Label>
              <Input data-testid="pf-image" value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})} className="bg-black border-white/10 mt-1" placeholder="https://... or leave blank"/>
            </div>
            <ImageUploader
              endpoint="product-image"
              value={form.images}
              onChange={(images) => {
                // If no main URL is set yet, first uploaded image becomes the main image URL
                if (images.length > 0 && (!form.image_url || form.image_url.includes("unsplash"))) {
                  setForm({ ...form, images, image_url: `${API}/files/${images[0]}` });
                } else {
                  setForm({ ...form, images });
                }
              }}
              max={8}
              label="Product photos (upload)"
            />
            <div><Label className="text-xs text-white/60">Description</Label><Textarea data-testid="pf-desc" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="bg-black border-white/10 mt-1" rows={4}/></div>
            <label className="flex items-center gap-2 text-sm text-white/70">
              <input data-testid="pf-featured" type="checkbox" checked={form.featured} onChange={(e) => setForm({...form, featured: e.target.checked})}/>
              Featured on home page
            </label>

            <div>
              <div className="flex items-center justify-between mt-2">
                <p className="mono-label text-white/50">Variants</p>
                <Button size="sm" variant="outline" data-testid="add-variant" onClick={addVar} className="border-white/20 bg-transparent hover:bg-white/5"><Plus className="h-3 w-3 mr-1"/>Add</Button>
              </div>
              <div className="mt-2 space-y-2">
                {form.variants.map((v, i) => (
                  <div key={i} className="grid grid-cols-[1fr,100px,80px,100px,auto] gap-2 items-end border border-white/10 p-2">
                    <div><Label className="text-[10px] text-white/50">Name</Label><Input value={v.name} onChange={(e) => updVar(i, "name", e.target.value)} className="bg-black border-white/10 mt-1 h-8"/></div>
                    <div><Label className="text-[10px] text-white/50">Price ₹</Label><Input type="number" value={v.price_inr} onChange={(e) => updVar(i, "price_inr", Number(e.target.value))} className="bg-black border-white/10 mt-1 h-8"/></div>
                    <div><Label className="text-[10px] text-white/50">Stock</Label><Input type="number" value={v.stock} onChange={(e) => updVar(i, "stock", Number(e.target.value))} className="bg-black border-white/10 mt-1 h-8"/></div>
                    <div><Label className="text-[10px] text-white/50">SKU</Label><Input value={v.sku} onChange={(e) => updVar(i, "sku", e.target.value)} className="bg-black border-white/10 mt-1 h-8"/></div>
                    <Button size="icon" variant="ghost" onClick={() => removeVar(i)}><X className="h-3 w-3"/></Button>
                  </div>
                ))}
              </div>
            </div>

            <Button data-testid="save-product" onClick={save} className="mt-2 bg-blue-600 hover:bg-blue-500 rounded-none">
              {editing ? "Update product" : "Create product"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
